import React, { ReactElement, ReactNode, useMemo, useState } from 'react';
import AlertModal from '../modals/AlertModal';

interface Props {
  children: any;
}

export type AlertTypes = 'Success' | 'Error' | 'Info' | 'Warning';

export interface AlertContextInterface {
  error: (info: AlertInfoInterface) => void;
  toggleAlert: () => void;
}

export interface AlertInfoInterface {
  type?: AlertTypes;
  title?: string | ReactNode;
  description?: string | ReactNode;
  footer?: ReactNode;
  /**
   * Stable machine name for WHICH alert this is, surfaced as `data-alert` on the modal.
   *
   * Every alert's title and description are translated copy, so without this an e2e can only tell
   * that *an* alert appeared — not whether the app diagnosed a throttle, a validation failure or an
   * unknown error. Those are different branches with different user consequences, and asserting on
   * the copy would tie the test to the active locale (and would silently pass if two branches ever
   * shared a message).
   */
  marker?: string;
}

export const AlertContext = React.createContext<AlertContextInterface>(null);

export default function AlertModalProvider(props: Props): ReactElement {
  const { children } = props;

  const [alertInfo, setAlertInfo] = useState<AlertInfoInterface>({
    title: '',
    description: '',
    footer: null,
  });

  const [showAlertModal, setShowAlertModal] = useState(false);

  function toggleAlertModal(info?: AlertInfoInterface) {
    if (info) {
      setShowAlertModal(true);
      setAlertInfo(info);

      return;
    }

    setShowAlertModal(false);
    setAlertInfo({
      title: '',
      description: '',
      footer: null,
    });
  }

  const renderAlertModal = useMemo(() => {
    return (
      <AlertModal
        openModal={showAlertModal}
        title={alertInfo?.title}
        description={alertInfo?.description}
        footer={alertInfo?.footer}
        marker={alertInfo?.marker}
        toggleModal={() => toggleAlertModal(null)}
      />
    );
  }, [showAlertModal, alertInfo]);

  const context: AlertContextInterface = useMemo(
    () => ({
      error: (info: AlertInfoInterface) => {
        toggleAlertModal({ ...info, type: 'Error' });
      },
      toggleAlert: () => setShowAlertModal((prev) => !prev),
    }),
    [setShowAlertModal],
  );

  return (
    <AlertContext.Provider value={context}>
      {children}
      {renderAlertModal}
    </AlertContext.Provider>
  );
}
